import { useState, useEffect, useRef } from 'react';
import { api } from '../../../api.js';

const PURPOSE_GROUPS = [
  ['Reports', [
    ['police_report', 'Police Report'],
    ['incident_report', 'Incident Report'],
    ['arrest_report', 'Arrest Report'],
    ['supplemental_report', 'Supplemental Report'],
    ['narrative_report', 'Narrative Report'],
    ['detective_report', 'Detective Report'],
    ['lab_report', 'Lab Report'],
    ['forensic_report', 'Forensic Report'],
    ['expert_report', 'Expert Report'],
    ['cad_report', 'CAD Report'],
    ['ems_report', 'EMS Report'],
  ]],
  ['Statements & Interviews', [
    ['witness_statement', 'Witness Statement'],
    ['victim_statement', 'Victim Statement'],
    ['suspect_statement', 'Suspect Statement'],
    ['interview_transcript', 'Interview Transcript'],
    ['interview_summary', 'Interview Summary'],
    ['confession', 'Confession'],
  ]],
  ['Correspondence', [
    ['correspondence', 'Correspondence'],
    ['email_thread', 'Email Thread'],
    ['email_message', 'Email Message'],
  ]],
  ['Legal Documents', [
    ['search_warrant', 'Search Warrant'],
    ['arrest_warrant', 'Arrest Warrant'],
    ['affidavit', 'Affidavit'],
    ['subpoena', 'Subpoena'],
    ['court_order', 'Court Order'],
    ['legal_filing', 'Legal Filing'],
    ['charging_document', 'Charging Document'],
    ['indictment', 'Indictment'],
    ['bond_document', 'Bond Document'],
  ]],
  ['Evidence & Forensics', [
    ['chain_of_custody', 'Chain of Custody'],
    ['evidence_log', 'Evidence Log'],
    ['property_record', 'Property Record'],
    ['autopsy_report', 'Autopsy Report'],
  ]],
  ['Medical Records', [
    ['medical_record', 'Medical Record'],
    ['hospital_record', 'Hospital Record'],
  ]],
  ['Media', [
    ['photo', 'Photo'],
    ['video', 'Video'],
    ['audio', 'Audio'],
    ['body_camera_video', 'Body Camera Video'],
    ['surveillance_video', 'Surveillance Video'],
  ]],
  ['Digital & Electronic', [
    ['cell_phone_extraction', 'Cell Phone Extraction'],
    ['social_media_record', 'Social Media Record'],
    ['text_message_record', 'Text Message Record'],
    ['call_detail_record', 'Call Detail Record'],
  ]],
  ['Booking & Custody', [
    ['booking_record', 'Booking Record'],
    ['jail_record', 'Jail Record'],
    ['custody_record', 'Custody Record'],
  ]],
  ['Administrative', [
    ['discovery_index', 'Discovery Index'],
    ['other', 'Other'],
  ]],
];

/**
 * ReclassifyPopover — anchored popover for bulk-setting document_type
 * on selected docs (was called "Categorize" in the old bar).
 */
export default function ReclassifyPopover({ caseId, ids, count, anchorRef, onClose, onCompleted }) {
  const [purpose, setPurpose] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorRef, onClose]);

  async function handleApply() {
    if (!purpose) {
      setError('Pick a type');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/documents/case/${caseId}/bulk-categorize`, {
        entries: ids.map((id) => ({ document_id: id, document_type: purpose })),
        reason: 'UI bulk reclassify',
      });
      onCompleted?.(`Reclassified ${count} file${count === 1 ? '' : 's'}`);
    } catch (err) {
      setError(err.message || 'Reclassify failed');
      setBusy(false);
    }
  }

  return (
    <div ref={ref} className="bulk-popover" role="dialog" aria-label="Reclassify documents">
      <div className="bulk-popover-title">Set type for {count} doc{count === 1 ? '' : 's'}:</div>
      <select
        className="bulk-popover-select"
        value={purpose}
        onChange={(e) => setPurpose(e.target.value)}
        disabled={busy}
      >
        <option value="">— Select type —</option>
        {PURPOSE_GROUPS.map(([groupLabel, entries]) => (
          <optgroup key={groupLabel} label={groupLabel}>
            {entries.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {error && <div className="bulk-popover-error">{error}</div>}
      <div className="bulk-popover-actions">
        <button type="button" className="btn btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleApply}
          disabled={busy || !purpose}
        >
          {busy ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </div>
  );
}
